import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  GalleryCategoriesContainer,
  GalleryHeader,
  GalleryListContainer,
  GalleryOverlay,
} from '@/components/route/listing/Gallery';
import HeadMeta from '@/components/static-pages/HeadMeta';
import { RV_SEARCH_PAGE } from '@/constants/urls';
import { useRouter } from '@/hooks/useRouter';
import { getCampgroundListingData } from '@/redux/selectors/campgrounds/page';
import { filterSafeQuery } from '@/utility/filterSafeQuery';

interface ICampgroundPhotosProps {
  isRVBundle?: boolean;
}

export const CampgroundPhotos = ({ isRVBundle }: ICampgroundPhotosProps) => {
  const intl = useIntl();
  const campground = useSelector(getCampgroundListingData);

  const router = useRouter();
  const safeQueryParams = filterSafeQuery({ ...router.query });

  const goToListing = useCallback(() => {
    router.push(
      {
        pathname: campground?.slug || RV_SEARCH_PAGE,
        query: safeQueryParams,
      },
      undefined,
      {
        shallow: true,
      },
    );
  }, [router, campground?.slug, safeQueryParams]);

  if (!campground) return null;

  const { name, description, slug, location } = campground || {};

  const pageTitle = intl.formatMessage(
    {
      defaultMessage: `Book Online Now at {campground} - in {locality}`,
      id: '4JFByw',
    },
    {
      campground: name,
      locality: `${location.city}, ${location.state}`,
    },
  );

  const pageSEODescription = description.slice(0, 200) + (description.length > 200 ? '...' : '');

  return (
    <>
      <HeadMeta
        title={pageTitle}
        description={pageSEODescription}
        canonicalUrl={process.env.hostApp + slug}
      />

      <main className="overflow-hidden">
        <GalleryOverlay
          goToListing={goToListing}
          headerComponent={<GalleryHeader title={name} />}
          categoriesComponent={<GalleryCategoriesContainer isCampground />}>
          <GalleryListContainer isCampground isRVBundle={isRVBundle} />
        </GalleryOverlay>
      </main>
    </>
  );
};
